import { useState, useEffect, useCallback, useMemo } from "react";
import { AdminSidebar } from "../Sidebar/Sidebar";
import { AdminTopBar } from "../topbar/Topbar";
import { Formik, Form, Field } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "react-bootstrap";
import "../Quest/Quests.css";
import "../School/Schools.css";
import BackButton from "../Goback/GoBack";
import TopImage from "../../common/TopImage";
import CustomInput from "../../../common/CustomInput";
import SuccessModal from "../../../common/SuccessModal";
import classroomImg from "../../../../assets/img/teacherDashboard/Classroom.png";
import { AgreementValidationSchema } from "./AgreementValidation";
import { useGetAgreementById } from "../../../../hooks/admin/agreement/useGetAgreementById";
import { useUpdateAgreement } from "../../../../hooks/admin/agreement/useUpdateAgreement";
import { useAssignQuestToSchool } from "../../../../hooks/admin/quest/useAssignQuestToSchool";
import { useGetSchoolQuestList } from "../../../../hooks/admin/school/useGetSchoolQuestList";
import Skeleton from "react-loading-skeleton";
import QuestSelector from "./QuestSelector";

export const AdminUpdateAgreement = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [inputLayout, setInputLayout] = useState(
    window.innerWidth < 576 ? "stacked" : "inline",
  );

  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Quest assignment states
  const [selectedQuestIds, setSelectedQuestIds] = useState([]);
  const [showAssignSuccessModal, setShowAssignSuccessModal] = useState(false);
  const [showAssignFailModal, setShowAssignFailModal] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const navigate = useNavigate();
  const { agreementId } = useParams();

  //  Fetch agreement details
  const { agreement, loading: agreementLoading } =
    useGetAgreementById(agreementId);

  // Update agreement API
  const { updateAgreement, loading, error } = useUpdateAgreement();

  //  Assign quests API
  const {
    assignQuestToSchool,
    loading: assignLoading,
    error: assignError,
  } = useAssignQuestToSchool();

  // Fetch school's already-assigned quests
  const {
    schoolQuestData,
    loading: schoolQuestLoading,
    getSchoolQuestList,
  } = useGetSchoolQuestList();

  // Once agreement is loaded, fetch the school's quest list
  useEffect(() => {
    if (agreement?.institution_id) {
      getSchoolQuestList(agreement.institution_id);
    }
  }, [agreement?.institution_id]);

  const preSelectedQuestIds = useMemo(() => {
    if (!schoolQuestData?.quests) return null;
    return new Set(schoolQuestData.quests.map((q) => q.id));
  }, [schoolQuestData]);

  useEffect(() => {
    const handleResize = () => {
      setInputLayout(window.innerWidth < 576 ? "stacked" : "inline");
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Quest selection callback
  const handleQuestSelectionChange = useCallback((ids) => {
    setSelectedQuestIds(ids);
  }, []);

  //  Initial values from API
  const initialValues = {
    institution_id: agreement?.institution_id || "",
    institution_name: agreement?.institution?.name || "",
    institution_currency: agreement?.currency || "",
    duration_value: agreement?.duration_value || "",
    duration_unit: agreement?.duration_unit || "year",
    base_licenses: agreement?.base_licenses || "",
    pricing_type: "bulk", // API not giving type
    price_per_unit: agreement?.price_per_unit || "",
    price_per_student: "",
    billing_mode: agreement?.billing_mode || "offline",
    price_type: agreement?.price_type || "bulk_pricing",
  };

  // Submit Handler
  const handleSubmit = async (values) => {
    const calculatedPrice =
      values.pricing_type === "student"
        ? Number(values.price_per_student) * Number(values.base_licenses)
        : Number(values.price_per_unit);

    const payload = {
      institution_id: Number(values.institution_id),
      billing_mode: values.billing_mode,
      duration_value: Number(values.duration_value),
      duration_unit: values.duration_unit,
      base_licenses: Number(values.base_licenses),
      price_per_unit: calculatedPrice,
    };

    await updateAgreement(agreementId, payload);
    setShowSuccessModal(true);
  };

  // Assign quests handler (standalone)
  const handleAssignQuests = async () => {
    if (!agreement?.institution_id || selectedQuestIds.length === 0) return;

    const questPayload = {
      quest_ids: selectedQuestIds,
      remark: "Assigned via agreement update",
    };

    try {
      await assignQuestToSchool(agreement.institution_id, questPayload);
      setShowAssignSuccessModal(true);
    } catch {
      setShowAssignFailModal(true);
    }
  };

  // Retry quest assignment
  const handleRetryAssign = async () => {
    if (!agreement?.institution_id) return;
    setRetrying(true);

    const questPayload = {
      quest_ids: selectedQuestIds,
      remark: "Assigned via agreement update",
    };

    try {
      await assignQuestToSchool(agreement.institution_id, questPayload);
      setShowAssignFailModal(false);
      setRetrying(false);
      setShowAssignSuccessModal(true);
    } catch {
      setRetrying(false);
    }
  };

  return (
    <div className="dashboardBody school-section">
      <AdminSidebar isSidebarCollapsed={isSidebarCollapsed} />

      <div className="container-fluid p-0">
        <div className="p-0" id="mainContent">
          <AdminTopBar toggleSidebar={toggleSidebar} />

          <div className="teacher-bodycontent mb-4">
            <section>
              <div className="container-fluid">
                <div className="row mb-3">
                  <div className="col-12">
                    <BackButton />
                  </div>
                </div>

                <TopImage imageSrc={classroomImg} text="View Agreement" />

                {agreementLoading ? (
                  <div className="school-card mb-4">
                    <Skeleton count={10} />
                  </div>
                ) : (
                  <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    validationSchema={AgreementValidationSchema}
                    onSubmit={handleSubmit}
                  >
                    {({ values }) => (
                      <Form className="mt-4">
                        <div className="school-card mb-4">
                          <h4 className="mb-4 fw-bold text-dark">
                            View Agreement
                          </h4>
                          <hr className="mb-4 text-muted" />

                          <div className="row g-3">
                            {/* School */}
                            <div className="col-md-12">
                              <CustomInput
                                label="School Name"
                                name="institution_name"
                                type="text"
                                layout={inputLayout}
                                labelWidth={{ desktop: "200px" }}
                                disabled
                              />
                            </div>

                            {/* Duration value */}
                            <div className="col-md-12">
                              <CustomInput
                                label="Duration"
                                name="duration_value"
                                type="number"
                                placeholder="Enter duration"
                                layout={inputLayout}
                                labelWidth={{ desktop: "200px" }}
                                disabled
                              />
                            </div>

                            {/* Duration unit */}
                            <div className="col-md-12">
                              <div
                                className={`custom-input-group ${
                                  inputLayout === "inline"
                                    ? "inline-layout"
                                    : ""
                                }`}
                                style={
                                  inputLayout === "inline"
                                    ? { "--label-width-desktop": "200px" }
                                    : {}
                                }
                              >
                                <div className="label-container">
                                  <label>Duration Unit *</label>
                                </div>

                                <div className="d-flex gap-4">
                                  <label className="d-flex align-items-center gap-2">
                                    <Field
                                      type="radio"
                                      name="duration_unit"
                                      value="year"
                                      disabled
                                    />
                                    Year
                                  </label>

                                  <label className="d-flex align-items-center gap-2">
                                    <Field
                                      type="radio"
                                      name="duration_unit"
                                      value="month"
                                      disabled
                                    />
                                    Month
                                  </label>
                                </div>
                              </div>
                            </div>

                            <div className="col-md-12">
                              <div
                                className={`custom-input-group ${
                                  inputLayout === "inline"
                                    ? "inline-layout"
                                    : ""
                                }`}
                                style={
                                  inputLayout === "inline"
                                    ? { "--label-width-desktop": "200px" }
                                    : {}
                                }
                              >
                                <div className="label-container">
                                  <label>Currency *</label>
                                </div>

                                <div className="d-flex gap-4">
                                  <label className="d-flex align-items-center gap-2">
                                    <Field
                                      type="radio"
                                      name="institution_currency"
                                      value="INR"
                                      disabled
                                    />
                                    INR
                                  </label>

                                  <label className="d-flex align-items-center gap-2">
                                    <Field
                                      type="radio"
                                      name="institution_currency"
                                      value="USD"
                                      disabled
                                    />
                                    USD
                                  </label>
                                </div>
                              </div>
                            </div>

                            {/* Licenses */}
                            <div className="col-md-12">
                              <CustomInput
                                label="No. of Licenses"
                                name="base_licenses"
                                type="number"
                                disabled
                                placeholder="Enter licenses"
                                layout={inputLayout}
                                labelWidth={{ desktop: "200px" }}
                              />
                            </div>

                            {/* Pricing type */}
                            <div className="col-md-12">
                              <div
                                className={`custom-input-group ${
                                  inputLayout === "inline"
                                    ? "inline-layout"
                                    : ""
                                }`}
                                style={
                                  inputLayout === "inline"
                                    ? { "--label-width-desktop": "200px" }
                                    : {}
                                }
                              >
                                <div className="label-container">
                                  <label>Pricing Type *</label>
                                </div>

                                <div className="d-flex gap-4">
                                  <label className="d-flex align-items-center gap-2">
                                    <Field
                                      type="radio"
                                      name="price_type"
                                      value="bulk_pricing"
                                      disabled
                                    />
                                    Bulk Pricing
                                  </label>

                                  <label className="d-flex align-items-center gap-2">
                                    <Field
                                      type="radio"
                                      name="price_type"
                                      value="per_student"
                                      disabled
                                    />
                                    Per Student
                                  </label>
                                </div>
                              </div>
                            </div>

                            {/* Bulk price */}
                            {values.pricing_type === "bulk" && (
                              <div className="col-md-12">
                                <CustomInput
                                  label="Price Per Unit"
                                  name="price_per_unit"
                                  type="number"
                                  placeholder="Enter price"
                                  layout={inputLayout}
                                  labelWidth={{ desktop: "200px" }}
                                  disabled
                                />
                              </div>
                            )}

                            {/* Student price */}
                            {values.pricing_type === "student" && (
                              <div className="col-md-12">
                                <CustomInput
                                  label="Price Per Student"
                                  name="price_per_student"
                                  type="number"
                                  placeholder="Enter student price"
                                  layout={inputLayout}
                                  labelWidth={{ desktop: "200px" }}
                                  disabled
                                />
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Quest Selector */}
                        <QuestSelector
                          onSelectionChange={handleQuestSelectionChange}
                          disabled={assignLoading}
                          preSelectedQuestIds={preSelectedQuestIds}
                          externalLoading={schoolQuestLoading}
                        />

                        {error && (
                          <span className="custom-error pb-2 d-block">
                            {error}
                          </span>
                        )}

                        <div className="row">
                          <div className="col-12">
                            <Button
                              type="submit"
                              hidden={true}
                              className="custom-primary-button"
                              disabled={loading}
                            >
                              {loading ? "Updating..." : "Update Agreement"}
                            </Button>
                          </div>
                        </div>

                        {/* Assign Quests Button */}
                        <div className="row mt-2">
                          <div className="col-12">
                            <Button
                              type="button"
                              className="custom-primary-button"
                              disabled={
                                assignLoading || selectedQuestIds.length === 0
                              }
                              onClick={handleAssignQuests}
                            >
                              {assignLoading
                                ? "Assigning Quests..."
                                : `Assign Quests (${selectedQuestIds.length})`}
                            </Button>
                          </div>
                        </div>
                      </Form>
                    )}
                  </Formik>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Success Modal (Agreement Update) */}
      <SuccessModal
        showModal={showSuccessModal}
        handleClose={() => setShowSuccessModal(false)}
        title="Agreement Updated Successfully"
        message="The agreement has been updated successfully."
        primaryButtonText="Agreement List"
        secondaryButtonText="Close"
        onPrimaryClick={() => navigate("/school/agreements")}
        onSecondaryClick={() => setShowSuccessModal(false)}
      />

      {/* Quest Assignment Success Modal */}
      <SuccessModal
        showModal={showAssignSuccessModal}
        handleClose={() => setShowAssignSuccessModal(false)}
        title="Quests Assigned Successfully"
        message="The selected quests have been assigned to the school successfully."
        primaryButtonText="Agreement List"
        secondaryButtonText="Close"
        onPrimaryClick={() => navigate("/school/agreements")}
        onSecondaryClick={() => setShowAssignSuccessModal(false)}
      />

      {/* Quest Assignment Failure Modal */}
      <SuccessModal
        showModal={showAssignFailModal}
        handleClose={() => setShowAssignFailModal(false)}
        type="error"
        title="Unable to Assign Quests"
        message="Quest assignment failed. You can retry the assignment or continue without it."
        primaryButtonText={retrying ? "Retrying..." : "Retry Assignment"}
        secondaryButtonText="Continue"
        onPrimaryClick={handleRetryAssign}
        onSecondaryClick={() => {
          setShowAssignFailModal(false);
        }}
      >
        {assignError && (
          <p className="text-danger text-center mb-0" style={{ fontSize: 13 }}>
            <i className="bi bi-exclamation-circle me-1" />
            {assignError}
          </p>
        )}
      </SuccessModal>
    </div>
  );
};
